<template>
  <div class="cont">
    <div style="width: 100%; display: flex; align-items: center;">
      <h2 class="page-title">Liked Songs</h2>
    </div>

    <transition name="fade" mode="out-in">
      <div v-if="isLoading" class="state-message">
        <div class="loader"></div>
      </div>
      <div v-else-if="!tracks.length" class="state-message">
        <v-icon size="48" color="#555">mdi-heart-outline</v-icon>
        <p>Songs you like will show up here.</p>
      </div>
      <div v-else class="list">
        <div
          class="listItem"
          v-for="track in likedTracks"
          :key="track.id"
          @click="play(track)"
        >
          <div class="thumb_cont">
            <div class="img-cont">
              <img class="thumbnail" :src="track.thumbnails?.[0]?.url" alt="" />
            </div>
          </div>
          <div class="info">
            <div style="text-align: left; width: 70%; overflow: hidden;">
              <p :class="isCurrentlyPlaying(track.id) ? 'highlight' : ''" class="track-title">{{ track.title }}</p>
              <p
                v-for="(artist, index) in track.artist"
                :key="index"
                class="artist-name"
                @click.stop="artist.id && $router.push(`/artist/${artist.id}`)"
              >{{ artist.name }}</p>
            </div>
            <LikeButton></LikeButton>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { useQueueStore } from '../stores/queue';
import { EventBus } from '../eventBus';
import { useLikedSongsStore } from '../stores/likedSongs';
import LikeButton from './likeButton.vue';

export default {
  name: 'LikedSongsComponent',
  components: { LikeButton },
  setup() {
    const queueStore = useQueueStore();
    const likedSongsStore = useLikedSongsStore();
    return { queueStore, likedSongsStore };
  },
  data() {
    return {
      toast: useToast(),
      prodUrl: process.env.VUE_APP_PROD_URL,
      isLoading: true,
      tracks: [],
    };
  },
  computed: {
    queue() {
      return this.queueStore.queue;
    },
    likedTracks() {
      return this.tracks.filter((t) => this.likedSongsStore.isLiked(t.id));
    },
  },
  mounted() {
    this.loadLikedSongs();
  },
  methods: {
    goBack() {
      if (window.history.state && window.history.state.back) {
        this.$router.back();
      } else {
        this.$router.push({ name: 'home' });
      }
    },
    isCurrentlyPlaying(videoId) {
      return this.queue[this.queueStore.isPlayingIndex]?.id === videoId;
    },
    async loadLikedSongs() {
      this.isLoading = true;
      try {
        const videoIds = await fetch(`${this.prodUrl}/user/likedSongs`, {
          credentials: 'include',
        }).then((res) => res.json());

        if (!Array.isArray(videoIds) || !videoIds.length) {
          this.tracks = [];
          return;
        }

        const infos = await Promise.all(
          videoIds.map((id) =>
            fetch(`${this.prodUrl}/music/getInfo?id=${id}`)
              .then((res) => res.json())
              .catch(() => null)
          )
        );

        this.tracks = infos
          .filter(Boolean)
          .map((info) => ({ ...info, id: info.id }));
      } catch (err) {
        console.error('Error loading liked songs:', err);
        this.toast.error('Could not load your liked songs.');
      } finally {
        this.isLoading = false;
      }
    },
    play(track) {
      this.queueStore.emptyQueue();
      this.queueStore.play();
      this.queueStore.isPlayingIndex = 0;
      EventBus.emit('play_track', track);
      if (!track.isAudioOnly) {
        this.$router.push(`/playing/${track.id}`);
      }
    },
  },
};
</script>

<style scoped>
.cont {
  width: 100%;
  height: 100%;
  color: white;
  padding: 0 4%;
  box-sizing: border-box;
  overflow-y: auto;
  scrollbar-width: none;
}

.page-title {
  margin-left: 12px;
  font-weight: 600;
  font-size: 1.4rem;
}

.state-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60%;
  color: #888;
  gap: 12px;
}

.list {
  margin-top: 2%;
  padding-bottom: 3%;
}

.listItem {
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: 6px 0;
  border-radius: 8px;
  transition: background 0.2s;
}

.listItem:hover {
  background: #ffffff0d;
}

.thumb_cont {
  width: 60px;
  flex-shrink: 0;
}

.img-cont {
  height: 50px;
  width: 50px;
  overflow: hidden;
  border-radius: 6px;
}

.info {
  width: calc(100% - 60px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 12px;
}

.track-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.artist-name {
  color: grey;
  font-size: small;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.artist-name:hover {
  color: #fff;
}

.highlight {
  color: #e50914;
}

@media (max-width: 675px) {
  .cont {
    padding: 0 2%;
  }
}
</style>
